import type { ReleasePr } from "../api/releases";
import { releaseChecks } from "./releaseCenter.ts";
import {
  headIdentity,
  definitiveApprovalRefusal,
  ownerApprovalFeedback,
  type OwnerMode,
  ownerRequestId,
  type OwnerResult,
  validOwnerReason,
  validPreparation,
  type WorkflowHead,
  type WorkflowPrepared,
} from "./workflowOwner.ts";
export type BulkTask = { id: string; title: string; heads: WorkflowHead[] };
export type BulkReview = {
  task: BulkTask;
  prepared?: WorkflowPrepared;
  error?: string;
};
export type BulkResult = {
  task: BulkTask;
  status: "approved" | "refused" | "uncertain" | "skipped";
  reason: string;
  receipt?: OwnerResult;
  request_id?: string;
};
export function bulkSelectionReason(prs: ReleasePr[]) {
  if (!prs.length) return "אין נתוני חבילה מלאים";
  for (const pr of prs) {
    if (
      pr.state !== "open" || pr.draft || pr.workflow_blocker?.code === "draft"
    ) return "טיוטה או שינוי שאינו פתוח";
    if (["DIRTY", "BEHIND"].includes(String(pr.merge_state).toUpperCase())) {
      return "קונפליקט או ענף שדורש עדכון";
    }
    if (pr.task_link_state !== "verified" || !pr.task?.id) {
      return "קישור המשימה טרם אומת";
    }
    if (releaseChecks(pr) !== "passed") {
      return releaseChecks(pr) === "failed"
        ? "בדיקות נכשלו"
        : "הבדיקות טרם אומתו";
    }
    if (
      pr.identity_only || pr.level === null || pr.detail_sha !== pr.head_sha ||
      pr.control_snapshot?.head_sha !== pr.head_sha ||
      pr.control_snapshot?.risk_reference_unverified ||
      pr.control_snapshot?.row_error
    ) return "הסיכון או הגרסה טרם אומתו";
    if (pr.migrations?.some((m) => m.status === "collision")) {
      return "התנגשות במיגרציה";
    }
    if (
      pr.workflow_blocker &&
      !["owner_approval", "screens_approval"].includes(pr.workflow_blocker.code)
    ) return pr.workflow_blocker.text;
  }
  return "";
}
export async function prepareBulkTasks(
  tasks: BulkTask[],
  prepare: (heads: WorkflowHead[]) => Promise<WorkflowPrepared>,
  current: (task: BulkTask) => boolean,
): Promise<BulkReview[]> {
  const reviews: BulkReview[] = [];
  for (const task of tasks) {
    try {
      if (!current(task)) throw Error("החבילה השתנתה; יש לרענן ולבחור מחדש");
      const prepared = await prepare(task.heads);
      if (!current(task) || !validPreparation(prepared, task.heads, task.id)) {
        throw Error("הגרסאות או חתימות החבילה לא אומתו; יש להכין מחדש");
      }
      reviews.push({
        task,
        prepared,
      });
    } catch (error) {
      reviews.push({
        task,
        error: error instanceof Error ? error.message : "הכנת החבילה נכשלה",
      });
    }
  }
  return reviews;
}
export async function approveBulkTasks(
  reviews: BulkReview[],
  intent: { mode: OwnerMode; reason: string; confirmed: boolean },
  deps: {
    storage: Pick<Storage, "getItem" | "setItem">;
    approve: (
      input: {
        prs: WorkflowHead[];
        fingerprint: string;
        mode: OwnerMode;
        reason: string;
        request_id: string;
      },
    ) => Promise<OwnerResult>;
    current: (task: BulkTask) => boolean;
    progress?: (results: BulkResult[]) => void;
  },
): Promise<BulkResult[]> {
  if (
    !intent.confirmed || !validOwnerReason(intent.reason) ||
    !["night", "emergency"].includes(intent.mode)
  ) throw Error("נדרשים מסלול, סיבה ואישור מפורש");
  const results: BulkResult[] = [];
  let stopped = false;
  for (const review of reviews) {
    const { task, prepared } = review;
    let sent = false, request_id: string | undefined;
    if (stopped) {
      results.push({
        task,
        status: "skipped",
        reason: "לא נשלח: הרצף נעצר עד לבירור התנאי הקודם",
      });
      deps.progress?.([...results]);
      continue;
    }
    try {
      if (
        review.error || !prepared ||
        !validPreparation(prepared, task.heads, task.id)
      ) throw Error(review.error ?? "החבילה טרם הוכנה ואומתה");
      if (!deps.current(task)) throw Error("החבילה השתנתה; האישור לא נשלח");
      const input = {
        prs: task.heads,
        fingerprint: prepared.fingerprint,
        mode: intent.mode,
        reason: intent.reason.trim(),
      };
      request_id = await ownerRequestId(deps.storage, input);
      if (!deps.current(task)) throw Error("החבילה השתנתה; האישור לא נשלח");
      sent = true;
      const receipt = await deps.approve({ ...input, request_id });
      results.push({
        task,
        request_id,
        receipt,
        status: receipt.approved ? "approved" : "refused",
        reason: ownerApprovalFeedback(receipt),
      });
      stopped = !receipt.approved ||
        Boolean(receipt.held || receipt.mirror_pending);
    } catch (error) {
      results.push({
        task,
        request_id,
        status: sent && !definitiveApprovalRefusal(error) ? "uncertain" : "refused",
        reason: sent && !definitiveApprovalRefusal(error)
          ? `${error instanceof Error ? error.message + '. ' : ''}התוצאה אינה ודאית; יש לבדוק את קבלת האישור לפני ניסיון חדש`
          : error instanceof Error
          ? error.message
          : "האישור לא נשלח",
      });
      stopped = true;
    }
    deps.progress?.([...results]);
  }
  return results;
}
export const bulkTaskIdentity = (task: BulkTask) =>
  JSON.stringify([task.id, headIdentity(task.heads)]);
